import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, Edit, Trash2, ClipboardList, AlertCircle, ExternalLink, Clock, MapPin, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREQUENTIES = [
  { value: 'dagelijks', label: 'Dagelijks', emoji: '📅', color: 'bg-amber-100 text-amber-700' },
  { value: 'wekelijks', label: 'Wekelijks', emoji: '📆', color: 'bg-purple-100 text-purple-700' },
  { value: 'maandelijks', label: 'Maandelijks', emoji: '📊', color: 'bg-teal-100 text-teal-700' },
];

const DAGEN_VAN_WEEK = [
  { value: 0, label: 'Maandag' },
  { value: 1, label: 'Dinsdag' },
  { value: 2, label: 'Woensdag' },
  { value: 3, label: 'Donderdag' },
  { value: 4, label: 'Vrijdag' },
  { value: 5, label: 'Zaterdag' },
  { value: 6, label: 'Zondag' },
];

import { LOCATIE_TYPES, formatLocatieCount, typeAangevinkt, typeToggle } from '@/lib/locatieTypes';

const Takenbeheer = () => {
  const [templates, setTemplates] = useState([]);
  const [locaties, setLocaties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchLocaties();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/taak-templates?include_inactive=true`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Fout bij ophalen taak templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocaties = async () => {
    try {
      const response = await axios.get(`${API}/locaties`);
      setLocaties(response.data);
    } catch (error) {
      console.error('Error fetching locaties:', error);
    }
  };

  const handleAdd = (frequentie = null) => {
    setEditingTemplate(frequentie ? { frequentie } : null);
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleDelete = (template) => {
    // Open the confirmation dialog instead of window.confirm
    setDeleteTarget(template);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/taak-templates/${deleteTarget._id}`);
      toast.success('Taak verwijderd');
      setDeleteTarget(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Fout bij verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActief = async (template) => {
    try {
      await axios.put(`${API}/taak-templates/${template._id}`, {
        actief: !template.actief
      });
      toast.success(template.actief ? 'Taak gedeactiveerd' : 'Taak geactiveerd');
      fetchTemplates();
    } catch (error) {
      console.error('Error toggling actief:', error);
      toast.error('Fout bij bijwerken');
    }
  };

  // Group templates by frequency
  const groupedTemplates = templates.reduce((acc, template) => {
    const freq = template.frequentie || 'dagelijks';
    if (!acc[freq]) acc[freq] = [];
    acc[freq].push(template);
    return acc;
  }, {});

  // Count stats
  const stats = {
    dagelijks: groupedTemplates['dagelijks']?.length || 0,
    wekelijks: groupedTemplates['wekelijks']?.length || 0,
    maandelijks: groupedTemplates['maandelijks']?.length || 0,
    // 4.7: rusttaken hebben hun eigen pagina en horen niet in deze tellingen
    actief: templates.filter(t => t.actief && t.type !== 'rust').length,
    inactief: templates.filter(t => !t.actief && t.type !== 'rust').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Takenbeheer</h1>
          <p className="text-slate-500">Beheer de standaard taken voor alle locaties</p>
        </div>
        <Button onClick={() => handleAdd()}>
          <Plus className="w-4 h-4 mr-2" />
          Nieuwe Taak
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900">
                <strong>Admin functie:</strong> Taken die hier worden toegevoegd worden automatisch gegenereerd 
                voor alle locaties op basis van hun frequentie (dagelijks/wekelijks/maandelijks).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {FREQUENTIES.map(freq => (
          <Card key={freq.value}>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-800">{stats[freq.value]}</div>
                <div className="text-sm text-slate-500 flex items-center justify-center gap-1">
                  <span>{freq.emoji}</span>
                  {freq.label}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{stats.actief}</div>
              <div className="text-sm text-slate-500">Actief</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-400">{stats.inactief}</div>
              <div className="text-sm text-slate-500">Inactief</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates by Frequency */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">Nog geen taak templates toegevoegd</p>
            <Button onClick={() => handleAdd()}>
              Eerste taak toevoegen
            </Button>
          </CardContent>
        </Card>
      ) : (
        FREQUENTIES.map(freq => {
          const freqTemplates = groupedTemplates[freq.value];
          if (!freqTemplates || freqTemplates.length === 0) return null;
          
          return (
            <Card key={freq.value}>
              <CardHeader className="bg-slate-50 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-xl">{freq.emoji}</span>
                    {freq.label}
                    <Badge className={freq.color}>
                      {freqTemplates.length} {freqTemplates.length === 1 ? 'taak' : 'taken'}
                    </Badge>
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdd(freq.value)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Taak toevoegen
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titel</TableHead>
                      <TableHead>Toepassing</TableHead>
                      <TableHead>Tijd</TableHead>
                      <TableHead>Extern</TableHead>
                      <TableHead>Volgorde</TableHead>
                      <TableHead>Actief</TableHead>
                      <TableHead className="text-right">Acties</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {freqTemplates
                      .sort((a, b) => (a.volgorde || 0) - (b.volgorde || 0))
                      .map((template) => (
                        <TableRow key={template._id}>
                          <TableCell>
                            <div>
                              <span className="font-medium">{template.titel}</span>
                              {template.isChecklist && (
                                <Badge variant="outline" className="ml-2 text-xs">Checklist</Badge>
                              )}
                            </div>
                            {template.beschrijving && (
                              <p className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">
                                {template.beschrijving}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <ToepassingBadge 
                              toepassingType={template.toepassingType}
                              specifiekLocaties={template.specifiekLocaties}
                              locatieTypes={template.locatieTypes}
                              locaties={locaties}
                            />
                          </TableCell>
                          <TableCell>
                            {template.standaardTijd ? (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Clock className="w-3 h-3" />
                                {template.standaardTijd}
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {template.isExtern ? (
                              <Badge className="bg-blue-100 text-blue-700 gap-1">
                                <ExternalLink className="w-3 h-3" />
                                Ja
                              </Badge>
                            ) : (
                              <span className="text-slate-400">Nee</span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-600">{template.volgorde || 0}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={template.actief}
                                onCheckedChange={() => handleToggleActief(template)}
                                className="scale-75"
                              />
                              <span className={`text-sm ${template.actief ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {template.actief ? 'Actief' : 'Inactief'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(template)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(template)}
                                data-testid={`delete-template-${template._id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })
      )}

      <TaakTemplateModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTemplate(null);
        }}
        template={editingTemplate}
        onUpdate={fetchTemplates}
        locaties={locaties}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Taak verwijderen</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je <strong>"{deleteTarget?.titel}"</strong> wilt verwijderen?
              <br /><br />
              <span className="text-red-600 font-medium">Let op:</span> Het template en alle eerder gegenereerde taken voor locaties worden permanent verwijderd. Dit kan niet ongedaan worden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="delete-cancel-btn">
              Annuleren
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="delete-confirm-btn">
              {deleting ? 'Verwijderen...' : 'Verwijderen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Toepassing Badge Component
const ToepassingBadge = ({ toepassingType, specifiekLocaties, locatieTypes, locaties }) => {
  const type = toepassingType || 'alle';
  
  if (type === 'alle') {
    return (
      <Badge className="bg-emerald-100 text-emerald-700 gap-1">
        <Globe className="w-3 h-3" />
        Alle locaties
      </Badge>
    );
  }
  
  if (type === 'locaties') {
    const count = specifiekLocaties?.length || 0;
    const locatieNamen = locaties
      .filter(l => specifiekLocaties?.includes(l._id))
      .map(l => l.naam)
      .slice(0, 3);
    const remaining = count - locatieNamen.length;
    
    return (
      <div className="flex flex-col gap-1">
        <Badge className="bg-blue-100 text-blue-700 gap-1">
          <MapPin className="w-3 h-3" />
          {count} {count === 1 ? 'locatie' : 'locaties'}
        </Badge>
        <span className="text-xs text-slate-500">
          {locatieNamen.join(', ')}
          {remaining > 0 && ` +${remaining}`}
        </span>
      </div>
    );
  }
  
  if (type === 'locatietypes') {
    const types = locatieTypes || [];
    return (
      <Badge className="bg-orange-100 text-orange-700 gap-1">
        <Building2 className="w-3 h-3" />
        {types.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')}
      </Badge>
    );
  }
  
  return <span className="text-slate-400">-</span>;
};

const TaakTemplateModal = ({ open, onClose, template, onUpdate, locaties }) => {
  const [formData, setFormData] = useState({
    titel: '',
    beschrijving: '',
    frequentie: 'dagelijks',
    standaardTijd: '',
    volgorde: 0,
    isExtern: false,
    externUrl: '',
    isChecklist: false,
    actief: true,
    toepassingType: 'alle',
    specifiekLocaties: [],
    locatieTypes: [],
    // Feature 4: Specific day fields
    dagVanWeek: null, // 0-6 for weekly tasks
    dagVanMaand: null, // 1-31 for monthly tasks
  });

  // Track if we need to update form data
  const [lastOpenState, setLastOpenState] = useState(false);
  
  // Update form data when modal opens with new template
  if (open && !lastOpenState) {
    // Schedule the state update for next render
    Promise.resolve().then(() => {
      const newFormData = template ? {
        titel: template.titel || '',
        beschrijving: template.beschrijving || '',
        frequentie: template.frequentie || 'dagelijks',
        standaardTijd: template.standaardTijd || '',
        volgorde: template.volgorde || 0,
        isExtern: template.isExtern || false,
        externUrl: template.externUrl || '',
        isChecklist: template.isChecklist || false,
        actief: template.actief !== undefined ? template.actief : true,
        toepassingType: template.toepassingType || 'alle',
        specifiekLocaties: template.specifiekLocaties || [],
        locatieTypes: template.locatieTypes || [],
        dagVanWeek: template.dagVanWeek !== undefined ? template.dagVanWeek : null,
        dagVanMaand: template.dagVanMaand !== undefined ? template.dagVanMaand : null,
      } : {
        titel: '',
        beschrijving: '',
        frequentie: 'dagelijks',
        standaardTijd: '',
        volgorde: 0,
        isExtern: false,
        externUrl: '',
        isChecklist: false,
        actief: true,
        toepassingType: 'alle',
        specifiekLocaties: [],
        locatieTypes: [],
        dagVanWeek: null,
        dagVanMaand: null,
      };
      setFormData(newFormData);
      setLastOpenState(true);
    });
  } else if (!open && lastOpenState) {
    Promise.resolve().then(() => {
      setLastOpenState(false);
    });
  }

  const handleSubmit = async () => {
    if (!formData.titel) {
      toast.error('Titel is verplicht');
      return;
    }
    if (!formData.frequentie) {
      toast.error('Frequentie is verplicht');
      return;
    }
    if (formData.isExtern && !formData.externUrl) {
      toast.error('Externe URL is verplicht voor externe taken');
      return;
    }
    if (formData.toepassingType === 'locaties' && formData.specifiekLocaties.length === 0) {
      toast.error('Selecteer minimaal 1 locatie');
      return;
    }
    if (formData.toepassingType === 'locatietypes' && formData.locatieTypes.length === 0) {
      toast.error('Selecteer minimaal 1 locatietype');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        externUrl: formData.isExtern ? formData.externUrl : null,
        // Clear irrelevant fields based on toepassingType
        specifiekLocaties: formData.toepassingType === 'locaties' ? formData.specifiekLocaties : [],
        locatieTypes: formData.toepassingType === 'locatietypes' ? formData.locatieTypes : [],
        // Clear day fields based on frequency
        dagVanWeek: formData.frequentie === 'wekelijks' ? formData.dagVanWeek : null,
        dagVanMaand: formData.frequentie === 'maandelijks' ? formData.dagVanMaand : null,
      };

      if (template?._id) {
        await axios.put(`${API}/taak-templates/${template._id}`, dataToSend);
        toast.success('Taak bijgewerkt');
      } else {
        await axios.post(`${API}/taak-templates`, dataToSend);
        toast.success('Taak toegevoegd');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Fout bij opslaan');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {template?._id ? 'Taak bewerken' : 'Nieuwe taak toevoegen'}
          </DialogTitle>
          <DialogDescription>
            Deze taak wordt automatisch gegenereerd voor alle locaties
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4 px-1">
          {/* Titel */}
          <div>
            <Label>Titel *</Label>
            <Input
              value={formData.titel}
              onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
              placeholder="Bijv. Kassa telling"
            />
          </div>

          {/* Beschrijving */}
          <div>
            <Label>Beschrijving</Label>
            <Input
              value={formData.beschrijving}
              onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
              placeholder="Optionele uitleg voor deze taak"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Frequentie */}
            <div>
              <Label>Frequentie *</Label>
              <Select
                value={formData.frequentie}
                onValueChange={(v) => setFormData({ ...formData, frequentie: v, dagVanWeek: null, dagVanMaand: null })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer frequentie" />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENTIES.map(freq => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.emoji} {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Standaard tijd */}
            <div>
              <Label>Standaard tijd</Label>
              <Input
                type="time"
                value={formData.standaardTijd}
                onChange={(e) => setFormData({ ...formData, standaardTijd: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">Optioneel: wanneer moet deze taak worden uitgevoerd?</p>
            </div>
          </div>

          {/* Feature 4: Specific day for weekly/monthly tasks */}
          {formData.frequentie === 'wekelijks' && (
            <div className="border-l-4 border-purple-300 pl-4 py-2 bg-purple-50 rounded-r-lg">
              <Label>Op welke dag van de week?</Label>
              <Select
                value={formData.dagVanWeek !== null ? String(formData.dagVanWeek) : 'default'}
                onValueChange={(v) => setFormData({ ...formData, dagVanWeek: v === 'default' ? null : parseInt(v) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Elke maandag (standaard)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Elke maandag (standaard)</SelectItem>
                  {DAGEN_VAN_WEEK.map(dag => (
                    <SelectItem key={dag.value} value={String(dag.value)}>
                      {dag.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Taak wordt gegenereerd op deze dag van elke week</p>
            </div>
          )}

          {formData.frequentie === 'maandelijks' && (
            <div className="border-l-4 border-teal-300 pl-4 py-2 bg-teal-50 rounded-r-lg">
              <Label>Op welke dag van de maand?</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="1"
                  max="31"
                  placeholder="1"
                  value={formData.dagVanMaand || ''}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 31) {
                      setFormData({ ...formData, dagVanMaand: value });
                    } else if (e.target.value === '') {
                      setFormData({ ...formData, dagVanMaand: null });
                    }
                  }}
                  className="w-24"
                />
                <span className="text-slate-500 text-sm">van elke maand</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Taak wordt gegenereerd op deze dag (standaard: 1e dag)</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Volgorde */}
            <div>
              <Label>Volgorde</Label>
              <Input
                type="number"
                value={formData.volgorde}
                onChange={(e) => setFormData({ ...formData, volgorde: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
              <p className="text-xs text-slate-500 mt-1">Bepaalt de sortering in de lijst</p>
            </div>
          </div>

          {/* Toepassing Section */}
          <div className="border-t pt-4">
            <Label className="text-base font-semibold mb-3 block">Toepassing</Label>
            <p className="text-sm text-slate-500 mb-4">Voor welke locaties is deze taak van toepassing?</p>
            
            <RadioGroup
              value={formData.toepassingType}
              onValueChange={(v) => setFormData({ ...formData, toepassingType: v })}
              className="space-y-3"
            >
              {/* Alle locaties */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="alle" id="alle" className="mt-1" />
                <div>
                  <Label htmlFor="alle" className="font-medium flex items-center gap-2 cursor-pointer">
                    <Globe className="w-4 h-4 text-emerald-600" />
                    Alle locaties
                  </Label>
                  <p className="text-xs text-slate-500">Taak geldt voor alle 20 locaties</p>
                </div>
              </div>

              {/* Specifieke locaties */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="locaties" id="locaties" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="locaties" className="font-medium flex items-center gap-2 cursor-pointer">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Specifieke locaties
                  </Label>
                  <p className="text-xs text-slate-500">Kies specifieke locaties</p>
                </div>
              </div>

              {/* Locatie selectie (alleen als "locaties" geselecteerd) */}
              {formData.toepassingType === 'locaties' && (
                <div className="pl-6 border-l-2 border-blue-200 ml-4">
                  <Label className="text-sm mb-2 block">Selecteer locaties:</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                    {locaties.map((loc) => (
                      <div key={loc._id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`loc-${loc._id}`}
                          checked={formData.specifiekLocaties.includes(loc._id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                specifiekLocaties: [...formData.specifiekLocaties, loc._id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                specifiekLocaties: formData.specifiekLocaties.filter(id => id !== loc._id)
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`loc-${loc._id}`} className="text-sm cursor-pointer">
                          {loc.naam}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {formData.specifiekLocaties.length} van {locaties.length} geselecteerd
                  </p>
                </div>
              )}

              {/* Per locatietype */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="locatietypes" id="locatietypes" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="locatietypes" className="font-medium flex items-center gap-2 cursor-pointer">
                    <Building2 className="w-4 h-4 text-orange-600" />
                    Per locatietype
                  </Label>
                  <p className="text-xs text-slate-500">Alle locaties van een bepaald type</p>
                </div>
              </div>

              {/* Locatietype selectie (alleen als "locatietypes" geselecteerd) */}
              {formData.toepassingType === 'locatietypes' && (
                <div className="pl-6 border-l-2 border-orange-200 ml-4">
                  <Label className="text-sm mb-2 block">Selecteer locatietypes:</Label>
                  <div className="space-y-2 p-2 bg-slate-50 rounded-lg">
                    {LOCATIE_TYPES.map((type) => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`type-${type.value}`}
                          checked={typeAangevinkt(formData.locatieTypes, type.value)}
                          onCheckedChange={(checked) => setFormData({
                            ...formData,
                            // 6.21: wasstraat = truckwash + legacy wasstraat
                            locatieTypes: typeToggle(formData.locatieTypes, type.value, !!checked)
                          })}
                          data-testid={`locatietype-${type.value}`}
                        />
                        <Label htmlFor={`type-${type.value}`} className="text-sm cursor-pointer">
                          {type.label} ({formatLocatieCount(locaties, type.value)})
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </RadioGroup>
          </div>

          {/* Toggles */}
          <div className="border-t pt-4 space-y-4">
            {/* Is Extern */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Externe link</Label>
                <p className="text-xs text-slate-500">Opent deze taak een externe website?</p>
              </div>
              <Switch
                checked={formData.isExtern}
                onCheckedChange={(v) => setFormData({ ...formData, isExtern: v })}
              />
            </div>

            {/* Externe URL (alleen als isExtern true is) */}
            {formData.isExtern && (
              <div className="pl-4 border-l-2 border-blue-200">
                <Label>Externe URL *</Label>
                <Input
                  value={formData.externUrl}
                  onChange={(e) => setFormData({ ...formData, externUrl: e.target.value })}
                  placeholder="https://daily-ops.nl"
                />
              </div>
            )}

            {/* Is Checklist */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Is checklist</Label>
                <p className="text-xs text-slate-500">Is dit een checklist met meerdere stappen?</p>
              </div>
              <Switch
                checked={formData.isChecklist}
                onCheckedChange={(v) => setFormData({ ...formData, isChecklist: v })}
              />
            </div>

            {/* Actief */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Actief</Label>
                <p className="text-xs text-slate-500">Inactieve taken worden niet gegenereerd</p>
              </div>
              <Switch
                checked={formData.actief}
                onCheckedChange={(v) => setFormData({ ...formData, actief: v })}
              />
            </div>
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit}>
            {template?._id ? 'Opslaan' : 'Toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Takenbeheer;
